import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import RatingDialog from "./RatingDialog";

interface Job {
  id: string;
  title: string;
  status: string;
  customer_id: string;
  tradesman_id: string | null;
}

interface Profile {
  id: string;
  full_name: string;
  user_type: string;
}

interface Rating {
  id: string;
  rating: number;
  written_feedback: string | null;
  created_at: string;
  reviewer: Profile;
}

interface JobRatingSectionProps {
  job: Job;
  currentUserProfile: Profile;
  onJobArchived?: () => void;
}

export default function JobRatingSection({ job, currentUserProfile, onJobArchived }: JobRatingSectionProps) {
  const [canRate, setCanRate] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [hasBeenRated, setHasBeenRated] = useState(false);
  const [otherPartyProfile, setOtherPartyProfile] = useState<Profile | null>(null);
  const [hasMessagesExchanged, setHasMessagesExchanged] = useState(false);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (job.status === 'completed') {
      checkRatingEligibility();
      fetchRatings();
    }
  }, [job, currentUserProfile]);

  const checkRatingEligibility = async () => {
    try {
      // Check if messages have been exchanged
      const { data: messages } = await supabase
        .from("messages")
        .select("id")
        .eq("job_id", job.id)
        .limit(1);

      const messagesExchanged = messages && messages.length > 0;
      setHasMessagesExchanged(messagesExchanged);

      // Get other party profile
      const otherPartyId = currentUserProfile.user_type === 'customer' 
        ? job.tradesman_id 
        : job.customer_id;

      if (otherPartyId) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("id, full_name, user_type")
          .eq("id", otherPartyId)
          .single();

        setOtherPartyProfile(profile);
      }

      // Check if current user has already rated the other party for this job
      const { data: existingRating } = await supabase
        .from("ratings")
        .select("id")
        .eq("job_id", job.id)
        .eq("reviewer_id", currentUserProfile.id)
        .eq("reviewee_id", otherPartyId)
        .limit(1)
        .maybeSingle();

      setHasRated(!!existingRating);

      // Check if current user has been rated for this job
      const { data: receivedRating } = await supabase
        .from("ratings")
        .select("id")
        .eq("job_id", job.id)
        .eq("reviewer_id", otherPartyId)
        .eq("reviewee_id", currentUserProfile.id)
        .limit(1)
        .maybeSingle();

      setHasBeenRated(!!receivedRating);

      // Can rate if: job completed and haven't rated yet
      setCanRate(job.status === 'completed' && !existingRating);

    } catch (error) {
      console.error("Error checking rating eligibility:", error);
    }
  };

  const fetchRatings = async () => {
    try {
      console.log('Fetching ratings for job:', job.id);
      const { data, error } = await supabase.rpc('get_job_ratings_with_reviewers', { job_id: job.id });

      if (error) {
        console.error('Error fetching ratings via RPC:', error);
        setRatings([]);
        return;
      }

      console.log('Raw RPC response:', data);

      const ratingsWithProfiles = (data || []).map((r: any) => ({
        id: r.id,
        rating: r.rating,
        written_feedback: r.written_feedback,
        created_at: r.created_at,
        reviewer: {
          id: r.reviewer_id,
          full_name: r.reviewer_name || 'Unknown User',
          user_type: r.reviewer_user_type || 'unknown',
        },
      }));

      setRatings(ratingsWithProfiles);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      setRatings([]);
    }
  };

  if (job.status !== 'completed') {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5 text-yellow-500" />
          Job Rating & Feedback
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Rating Status */}
        <div className="space-y-2">
          {!hasMessagesExchanged && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm">No messages exchanged</span>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {hasRated && (
              <Badge variant="outline" className="text-green-600 border-green-600">
                You rated this job
              </Badge>
            )}
            {hasBeenRated && (
              <Badge variant="outline" className="text-blue-600 border-blue-600">
                You received a rating
              </Badge>
            )}
            {canRate && (
              <Button
                onClick={() => {
                  const otherId = currentUserProfile.user_type === 'customer' ? job.tradesman_id : job.customer_id;
                  if (!otherId) {
                    toast({
                      title: "Cannot open rating",
                      description: currentUserProfile.user_type === 'customer' ? "No tradesman assigned to this completed job." : "Customer not found for this job.",
                      variant: "destructive",
                    });
                    return;
                  }
                  setShowRatingDialog(true);
                }}
                size="sm"
              >
                Rate {currentUserProfile.user_type === 'customer' ? 'Tradesman' : 'Customer'}
              </Button>
            )}
          </div>
        </div>

        {/* Existing Ratings */}
        {ratings.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">Feedback for this job:</h4>
            {ratings.map((rating) => (
              <div key={rating.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{rating.reviewer.full_name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {rating.reviewer.user_type}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= rating.rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground"
                        }`}
                      />
                    ))}
                  </div>
                </div>
                {rating.written_feedback && (
                  <p className="text-sm text-muted-foreground">{rating.written_feedback}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {new Date(rating.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Rating Dialog */}
        {showRatingDialog && (currentUserProfile.user_type === 'customer' ? job.tradesman_id : job.customer_id) && (
          <RatingDialog
            isOpen={showRatingDialog}
            onClose={() => {
              setShowRatingDialog(false);
              checkRatingEligibility();
              fetchRatings();
            }}
            onJobArchived={onJobArchived}
            jobId={job.id}
            revieweeId={(currentUserProfile.user_type === 'customer' ? job.tradesman_id : job.customer_id) as string}
            revieweeName={currentUserProfile.user_type === 'customer' ? 'Tradesman' : 'Customer'}
            revieweeType={(currentUserProfile.user_type === 'customer' ? 'tradesman' : 'customer')}
          />
        )}
      </CardContent>
    </Card>
  );
}
